'use client';

import type { SavedDataTableView } from 'src/features/data-table-views';
import { isNil } from 'es-toolkit';
import { LOCAL_STORAGE_KEYS } from 'src/constants/local-storage';
import {
  getActiveDataTableViewId,
  setActiveDataTableViewId,
  useSavedDataTableViews,
} from 'src/features/data-table-views';

import type { StrategyType } from '../utils/accounts-url-state';

export const MAX_SAVED_ACCOUNT_FILTERS = 20;

export type SavedAccountFilterFields = {
  readonly balanceMin: number | null;
  readonly balanceMax: number | null;
  readonly strategyType: readonly StrategyType[];
  readonly strategy: readonly string[];
  readonly feeSchedule: readonly string[];
  readonly household: readonly string[];
  // Optional: saved filters created before these fields existed won't have them.
  readonly advisorIds?: readonly string[];
  readonly includeUnassignedAdvisor?: boolean;
};

export type SavedAccountFilter = SavedDataTableView<SavedAccountFilterFields>;

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((entry) => typeof entry === 'string');
}

function isNumberOrNull(value: unknown): value is number | null {
  return value === null || typeof value === 'number';
}

function isBoolean(value: unknown): value is boolean {
  return typeof value === 'boolean';
}

function isStrategyType(value: unknown): value is StrategyType {
  return value === 'first_party' || value === 'third_party' || value === 'unassigned';
}

function isStrategyTypeArray(value: unknown): value is StrategyType[] {
  return Array.isArray(value) && value.every((entry) => isStrategyType(entry));
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

export function isSavedAccountFilterFields(value: unknown): value is SavedAccountFilterFields {
  if (!isRecord(value)) return false;

  return (
    isNumberOrNull(value['balanceMin']) &&
    isNumberOrNull(value['balanceMax']) &&
    isStrategyTypeArray(value['strategyType']) &&
    isStringArray(value['strategy']) &&
    isStringArray(value['feeSchedule']) &&
    isStringArray(value['household']) &&
    // `advisorIds`/`includeUnassignedAdvisor` were added after this validator shipped;
    // tolerate their absence on saved filters created before these fields existed
    // rather than dropping them.
    (isNil(value['advisorIds']) || isStringArray(value['advisorIds'])) &&
    (isNil(value['includeUnassignedAdvisor']) || isBoolean(value['includeUnassignedAdvisor']))
  );
}

export function getActiveAccountFilterPresetId(): string | null {
  return getActiveDataTableViewId(LOCAL_STORAGE_KEYS.ACCOUNTS_ACTIVE_FILTER_PRESET_ID);
}

export function setActiveAccountFilterPresetId(id: string | null): void {
  setActiveDataTableViewId({ storageKey: LOCAL_STORAGE_KEYS.ACCOUNTS_ACTIVE_FILTER_PRESET_ID, id });
}

type UseSavedAccountFiltersReturn = {
  readonly savedFilters: SavedAccountFilter[];
  readonly atLimit: boolean;
  readonly saveFilter: (name: string, filters: SavedAccountFilterFields) => SavedAccountFilter | null;
  readonly updateFilter: (id: string, filters: SavedAccountFilterFields) => void;
  readonly renameFilter: (id: string, name: string) => void;
  readonly deleteFilter: (id: string) => SavedAccountFilter[];
};

export function useSavedAccountFilters(): UseSavedAccountFiltersReturn {
  const { savedViews, atLimit, saveView, updateView, renameView, deleteView } = useSavedDataTableViews({
    storageKey: LOCAL_STORAGE_KEYS.ACCOUNTS_SAVED_FILTERS,
    filtersValidator: isSavedAccountFilterFields,
    maxSavedViews: MAX_SAVED_ACCOUNT_FILTERS,
  });

  return {
    savedFilters: savedViews,
    atLimit,
    saveFilter: saveView,
    updateFilter: updateView,
    renameFilter: renameView,
    deleteFilter: deleteView,
  };
}
